/**
 * dashboardTransforms.js
 *
 * Converts raw responses from EXISTING, CONFIRMED backend endpoints into
 * the exact prop shapes the dashboard card components expect.
 *
 * Every function here is built only from fields that are actually on the
 * models today (project/models.py, inventory/models.py, quiz app, training
 * app). Nothing here depends on backend changes.
 *
 * Endpoints used:
 *   GET /project/                       -> ProjectList        (Project[])
 *   GET /machine_reservation_list/      -> MachineReservationList
 *   GET /workbench_bookings/            -> BookWorkbenchList
 *   GET /admin_machine_list/            -> AdminMachineList (Machine[])
 *   GET /quizzes/                       -> QuizListView (Quiz[])
 *   GET /quizzes/:id/attempts-remaining/-> per-quiz attempt status
 *   (training app) workshop list endpoint -> Workshop[]
 */

import {
    reservationStatusLabel,
    projectStatusLabel,
    machineStatusLabel,
    quizStatusLabel,
    workshopStatusLabel,
    formatDate,
} from "./Common/statusUtils";

/* ---------------------------- Projects ---------------------------- */

// Project model has no team NAME field, only a `teammates` M2M — the
// mockup's "team" line is derived here as a headcount, not a real name.
export function mapProjectToCard(project) {
    const projectTypes = {
        "1": "Academic",
        "2": "Research",
        "3": "Personal",
        "4": "Other",
    };

    const progressMap = {
        "1": 0,     // Not Started
        "2": 50,    // In Progress
        "3": 100,   // Completed
    };

    return {
        id: project.id,

        title: project.title,

        // NEW
        type: projectTypes[project.type] || "Other",

        status: projectStatusLabel(project.progress_status),

        progress: progressMap[project.progress_status] ?? 0,

        // Keep these for future improvements
        teammateCount: project.teammates_names?.length || 0,
        teammates: project.teammates_names || [],

        patentFiled: project.is_patent_filed,

        budget: project.budget,

        expenditure: project.expenditure,

        duration: project.duration,

        platform: project.platform,
    };
}

export function mapProjectsSummary(projects) {
    const total = projects.length;
    const completed = projects.filter((p) => p.progress_status === "3").length;
    const active = total - completed;
    const solo = projects.filter((p) => !p.teammates_names?.length).length;
    const team = total - solo;

    return { total, active, completed, solo, team };
}

/* --------------------------- Tutorials/Quizzes --------------------------- */

// Pass in the Quiz object plus its matching attempt (from the
// attempts-remaining endpoint, fetched per quiz id).
export function mapQuizToTutorialCard(quiz, attempt) {
    const label = quizStatusLabel(attempt);

    return {
        id: quiz.id,
        title: quiz.name || quiz.title,
        unlocks: null,
        progress: attempt?.passed ? 100 : attempt?.score ?? 0,
        status: label,
        buttonText: label === "Quiz Failed" ? "Retake Quiz" : "Continue",
    };
}

/* ----------------------------- Workshops ----------------------------- */

export function mapWorkshopToCard(workshop) {
    const status = workshopStatusLabel(workshop.from_date, workshop.to_date);

    return {
        id: workshop.id,
        title: workshop.title,
        // No duration/seats field on Workshop model — compute a date
        // range instead of fabricating hours/seats.
        duration: `${formatDate(workshop.from_date)} - ${formatDate(
            workshop.to_date
        )}`,
        seats: null, // TutorialCard/WorkshopCard should hide if null
        status,
        buttonText: status === "Upcoming" ? "Register" : "Join Now",
    };
}

/* ----------------------------- Bookings ----------------------------- */

export function mapReservationToBookingRow(reservation) {
    return {
        id: reservation.id,
        title: reservation.machine?.name || "Machine",
        status: reservationStatusLabel(reservation.approved_status),
        date: formatDate(reservation.reserved_date),
    };
}

// inventory issue

export function isOverdue(dueDateStr) {
    if (!dueDateStr) return false;

    const due = new Date(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return due < today;
}

/* -------------------------- Needs Attention -------------------------- */

// "Needs attention" items built from real, existing data: rejected
// reservations, rejected workbench bookings, and overdue (unreturned)
// inventory issues.
//
// Machine/equipment borrow-return overdue tracking is NOT included here
// because no such borrow/return model exists yet for Machine/Equipment —
// that requires a backend change (see conversation history).
export function buildAttentionItems({
    reservations = [],
    workbenchBookings = [],
    inventoryIssues = [],
}) {
    const items = [];

    reservations
        .filter((r) => r.approved_status === "2")
        .forEach((r) => {
            items.push({
                id: `reservation-${r.id}`,
                title: "Booking Rejected",
                description: `${r.name} reservation on ${formatDate(
                    r.reserved_date
                )} was rejected`,
                action: "View Booking",
                status: "Rejected",
            });
        });

    workbenchBookings
        .filter((b) => b.approved_status === "2")
        .forEach((b) => {
            items.push({
                id: `workbench-${b.id}`,
                title: "Workbench Booking Rejected",
                description: `Booking on ${formatDate(
                    b.booking_date
                )} was rejected`,
                action: "View Booking",
                status: "Rejected",
            });
        });

    inventoryIssues
        .filter((i) => i.returned_ === "No" && isOverdue(i.issued_till))
        .forEach((i) => {
            items.push({
                id: `inventory-${i.id}`,
                title: "Item Overdue",
                description: `${i.name} was due on ${formatDate(
                    i.issued_till
                )}`,
                action: "Return Item",
                status: "Overdue",
            });
        });

    return items;
}

/* ------------------------------ Machines ------------------------------ */

// There is no "saved/favorited machine" relation on the User model yet,
// so this can't be a true "Saved Machines" list. It IS real machine data
// though — status1/availability1 both exist on Machine. Ships as a
// working "browse machines" card; rename to Saved Machines once the
// backend adds a favorites relation and this can filter by it.
export function mapMachineToCard(machine) {
    return {
        id: machine.id,
        name: machine.name,
        status: machineStatusLabel(machine.status1),
        category:
            typeof machine.category === "string"
                ? machine.category
                : machine.category?.name || null,
    };
}